import React, { useEffect, useMemo, useRef, useState } from 'react';
import { compareText, normalizeSearchTerm } from '../navigationUtils';

const SearchableSelector = ({
  id,
  label,
  items = [],
  selectedId,
  onSelect,
  searchValue,
  onSearchChange,
  searchPlaceholder,
  selectionPlaceholder,
  emptyMessage,
  required = false,
  disabled = false,
}) => {
  const containerRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const selectedItem = items.find((item) => item.id === selectedId) || null;

  const filteredItems = useMemo(() => {
    const searchTerm = normalizeSearchTerm(searchValue);
    const sortedItems = [...items].sort((left, right) => compareText(left.label, right.label));

    if (!searchTerm) {
      return sortedItems;
    }

    return sortedItems.filter((item) =>
      `${item.label} ${item.description || ''} ${item.searchText || ''}`.toLowerCase().includes(searchTerm)
    );
  }, [items, searchValue]);

  useEffect(() => {
    const handlePointerDown = (event) => {
      if (!containerRef.current?.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
    };
  }, []);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const selectedIndex = filteredItems.findIndex((item) => item.id === selectedId);
    setHighlightedIndex(selectedIndex >= 0 ? selectedIndex : 0);
  }, [filteredItems, isOpen, selectedId]);

  const selectItem = (item) => {
    onSelect(item.id);
    setIsOpen(false);
  };

  const handleKeyDown = (event) => {
    if (disabled) {
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();

      if (!isOpen) {
        setIsOpen(true);
        setHighlightedIndex(0);
        return;
      }

      setHighlightedIndex((previousIndex) =>
        Math.min(previousIndex + 1, Math.max(filteredItems.length - 1, 0))
      );
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();

      if (!isOpen) {
        setIsOpen(true);
        setHighlightedIndex(Math.max(filteredItems.length - 1, 0));
        return;
      }

      setHighlightedIndex((previousIndex) => Math.max(previousIndex - 1, 0));
      return;
    }

    if (event.key === 'Enter') {
      if (!isOpen) {
        event.preventDefault();
        setIsOpen(true);
        return;
      }

      if (filteredItems[highlightedIndex]) {
        event.preventDefault();
        selectItem(filteredItems[highlightedIndex]);
      }
      return;
    }

    if (event.key === 'Escape') {
      setIsOpen(false);
    }
  };

  return (
    <div className="df-searchable-selector" ref={containerRef}>
      <label className="form-label" htmlFor={id}>
        {label} {required && <span style={{ color: 'var(--error)' }}>*</span>}
      </label>

      <div className="df-searchable-selector-shell">
        {selectedItem ? (
          <div className="df-selector-selection">
            <span className="df-selector-selection-label">Selected</span>
            <div className="df-selector-selection-value">{selectedItem.label}</div>
            {selectedItem.description && (
              <div className="df-selector-selection-note">{selectedItem.description}</div>
            )}
          </div>
        ) : (
          <div className="df-selector-placeholder">{selectionPlaceholder}</div>
        )}

        <div className="form-group" style={{ marginBottom: 0 }}>
          <input
            id={id}
            type="text"
            className="form-input"
            value={searchValue}
            onChange={(event) => {
              onSearchChange(event.target.value);
              setIsOpen(true);
            }}
            onFocus={() => setIsOpen(true)}
            onKeyDown={handleKeyDown}
            placeholder={searchPlaceholder}
            disabled={disabled}
            autoComplete="off"
          />
        </div>

        <div className="df-selector-results-count">
          {filteredItems.length} result{filteredItems.length === 1 ? '' : 's'}
        </div>

        {isOpen && (
          <div className="df-selector-dropdown" role="listbox">
            {filteredItems.length === 0 ? (
              <div className="df-selector-empty">{emptyMessage}</div>
            ) : (
              filteredItems.map((item, index) => (
                <button
                  key={item.id}
                  type="button"
                  className={`df-selector-option ${
                    item.id === selectedId ? 'is-selected' : ''
                  } ${index === highlightedIndex ? 'is-highlighted' : ''}`}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => selectItem(item)}
                >
                  <div className="df-selector-option-title">{item.label}</div>
                  {item.description && (
                    <div className="df-selector-option-subtitle">{item.description}</div>
                  )}
                </button>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchableSelector;

